/**
 * poolErrors · short, human toasts for Lead Pool refusals (PLAN §4.8 + amendments).
 *
 * The server's errorCode decides the headline; its message (minus any raw "ALREADY_CLAIMED: "
 * style prefix) is the detail when it adds something. Unknown refusals fall back to the server's
 * own wording rather than a guess.
 */
import type { MutationResult } from './hooks/useLeadActions'

export interface ToastCopy {
  title: string
  description?: string
}

/** Drop a leading `SOME_CODE:` the backend sometimes puts in front of a readable sentence. */
export function cleanServerMessage(message?: string | null): string | undefined {
  if (!message) return undefined
  const cleaned = message.replace(/^[A-Z][A-Z0-9_]+:\s*/, '').trim()
  return cleaned || undefined
}

type Refusal = Pick<MutationResult<unknown>, 'errorCode' | 'status' | 'message'> & {
  /** The error body's `details` (or the body itself) — where the server puts refusal counts. */
  details?: unknown
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null
}

function firstNumber(rec: Record<string, unknown> | null, keys: string[]): number | undefined {
  if (!rec) return undefined
  for (const k of keys) {
    const v = rec[k]
    const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN
    if (Number.isFinite(n)) return n
  }
  return undefined
}

/**
 * The counts on a 422 HOLDING_CAP_REACHED (dispatch §3 says "with counts" without naming them),
 * so the common spellings are all read: `held` / `holding` / `untouched` / `untouchedClaims`, and
 * `cap` / `holdingCap` / `limit`.
 */
export function holdingCapCounts(details: unknown): { held?: number; cap?: number } {
  const rec = asRecord(details)
  return {
    held: firstNumber(rec, ['held', 'holding', 'untouched', 'untouchedClaims', 'current']),
    cap: firstNumber(rec, ['cap', 'holdingCap', 'limit', 'max']),
  }
}

/** "You're holding 10 untouched leads — work them before claiming more (cap 10)". */
export function holdingCapCopy(details: unknown): ToastCopy {
  const { held, cap } = holdingCapCounts(details)
  const heldPart = held != null ? `${held} untouched ${held === 1 ? 'lead' : 'leads'}` : 'too many untouched leads'
  const capPart = cap != null ? ` (cap ${cap})` : ''
  return {
    title: `You're holding ${heldPart} — work them before claiming more${capPart}`,
    description: 'Log a call, email or note on the ones you hold, then claim again.',
  }
}

export function claimErrorCopy(r: Refusal): ToastCopy {
  const detail = cleanServerMessage(r.message)
  if (r.errorCode === 'HOLDING_CAP_REACHED') {
    return holdingCapCopy(r.details)
  }
  if (r.errorCode === 'NOTHING_TO_CLAIM') {
    return { title: 'Nothing to claim right now', description: 'Every hot lead you can see already has an owner.' }
  }
  if (r.errorCode === 'CUSTOMER_TYPE_REQUIRED') {
    return { title: 'Set the customer type first', description: detail }
  }
  if (r.errorCode === 'ALREADY_CLAIMED' || r.status === 409) {
    return { title: 'Someone else claimed it first', description: 'It already has an owner.' }
  }
  if (r.errorCode === 'ACCOUNT_COMPANY') {
    return { title: 'This company is already an account', description: 'Accounts are not claimed from the pool.' }
  }
  if (r.errorCode === 'INVALID_CUSTOMER_TYPE') {
    return { title: 'Pick Direct customer or Freight forwarder' }
  }
  if (r.errorCode === 'CUSTOMER_UPDATE_REJECTED') {
    return { title: 'The company record refused the type', description: detail }
  }
  if (r.status === 403) {
    // The server says which refusal it is (reclaim lock, not visible, no permission).
    return { title: "You can't claim this one", description: detail ?? 'Ask your manager to assign it to you.' }
  }
  if (r.status === 404) {
    return { title: 'No longer in the pool', description: 'It may have been claimed or closed.' }
  }
  return { title: "Couldn't claim this opportunity", description: detail }
}

export function setTypeErrorCopy(r: Refusal): ToastCopy {
  const detail = cleanServerMessage(r.message)
  if (r.status === 403) {
    return { title: "You can't set the type on this record" }
  }
  if (r.errorCode === 'CUSTOMER_UPDATE_REJECTED') {
    return { title: 'The company record refused the change', description: detail }
  }
  if (r.errorCode === 'ACCOUNT_COMPANY') {
    return { title: 'This company is already an account', description: 'Its type is managed on the account.' }
  }
  if (r.errorCode === 'INVALID_CUSTOMER_TYPE') {
    return { title: 'Pick Direct customer or Freight forwarder' }
  }
  if (r.status === 503) {
    return { title: "Couldn't reach company records", description: 'Try again in a moment.' }
  }
  if (r.status === 404) {
    return { title: 'No longer in the pool', description: 'It may have been claimed or closed.' }
  }
  return { title: "Couldn't set the customer type", description: detail }
}

/** One line for a per-row failure inside a bulk result. */
export function bulkFailureLine(f: { errorCode?: string | null; message?: string | null }): string {
  const copy = setTypeErrorCopy({ errorCode: f.errorCode ?? undefined, message: f.message ?? undefined })
  return copy.description ? `${copy.title} — ${copy.description}` : copy.title
}

/** Claim-next: a 404 here means the queue is empty, not that a lead vanished. */
export function claimNextErrorCopy(r: Refusal): ToastCopy {
  if (r.errorCode === 'NOTHING_TO_CLAIM' || (r.status === 404 && !r.errorCode)) {
    return claimErrorCopy({ ...r, errorCode: 'NOTHING_TO_CLAIM' })
  }
  return claimErrorCopy(r)
}

/** The head's Assign on the dispatch board (POST /leads/pool/{leadId}/assign). */
export function assignErrorCopy(r: Refusal): ToastCopy {
  const detail = cleanServerMessage(r.message)
  if (r.errorCode === 'ASSIGNEE_NOT_SALES') {
    return { title: "Couldn't assign", description: 'Only sales team members can own opportunities.' }
  }
  if (r.errorCode === 'CUSTOMER_TYPE_REQUIRED') {
    return { title: 'Set the customer type first', description: detail }
  }
  if (r.errorCode === 'ALREADY_CLAIMED' || r.status === 409) {
    return { title: 'Someone claimed it first', description: 'It already has an owner.' }
  }
  if (r.status === 403) {
    return { title: "You can't assign this one", description: detail ?? 'Assigning from the pool is a manager action.' }
  }
  if (r.status === 404) {
    return { title: 'No longer in the pool', description: 'It may have been claimed or closed.' }
  }
  return { title: "Couldn't assign", description: detail }
}
